import { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Form,
  Radio,
  Select,
  DatePicker,
  Input,
  Button,
  Alert,
  Space,
  Typography,
  Empty,
  Tag,
  Divider,
} from 'antd';
import { SkinOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AbcdeConclusion, type AbcdeEvaluation, type CreateAbcdeDto } from '../../../../api/dermatoApi';
import { useAuthStore } from '../../../../store/authStore';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import dermatoApi from '../../../../api/dermatoApi';

const { Text } = Typography;

const CONCLUSION_OPTIONS = Object.values(AbcdeConclusion).map((v) => ({
  value: v,
  label:
    v === 'NO_SUSPICIOUS_FEATURES'
      ? 'Fără trăsături suspecte'
      : v === 'REQUIRES_DERMOSCOPY'
        ? 'Necesită dermatoscopie'
        : 'Leziune suspectă',
}));

// D2.2 — o singură alegere per literă: Absentă / Prezentă / Necunoscut.
// Payload-ul salvat rămâne cele 10 Boolean-uri din schema Prisma existentă.
type AbcdeChoice = 'absent' | 'present' | 'unknown';

type LetterField = 'A' | 'B' | 'C' | 'D' | 'E';

type LetterConfig = {
  letter: LetterField;
  title: string;
  /** Câmpul „absent/regular/uniform/less6mm/stable" din DTO. */
  absentKey: keyof CreateAbcdeDto;
  /** Câmpul „present/irregular/nonuniform/6mm/modified" din DTO. */
  presentKey: keyof CreateAbcdeDto;
};

const LETTERS: LetterConfig[] = [
  { letter: 'A', title: 'Asimetrie (A)', absentKey: 'asymmetryAbsent', presentKey: 'asymmetryPresent' },
  { letter: 'B', title: 'Margini (B)', absentKey: 'borderRegular', presentKey: 'borderIrregular' },
  { letter: 'C', title: 'Culoare (C)', absentKey: 'colorUniform', presentKey: 'colorNonUniform' },
  { letter: 'D', title: 'Diametru (D)', absentKey: 'diameterLess6mm', presentKey: 'diameter6mm' },
  { letter: 'E', title: 'Evoluție (E)', absentKey: 'evolutionStable', presentKey: 'evolutionModified' },
];

/**
 * Mapare alegere UI → perechea de Boolean din DTO:
 *  Absentă   → (true,  false)
 *  Prezentă  → (false, true)
 *  Necunoscut → (false, false)
 */
const toPair = (choice: AbcdeChoice): [boolean, boolean] => {
  if (choice === 'absent') return [true, false];
  if (choice === 'present') return [false, true];
  return [false, false];
};

/**
 * Mapare inversă — perechea de Boolean din evaluarea existentă → alegere UI.
 *  (true,  false) → 'absent'
 *  (false, true)  → 'present'
 *  orice altă combinație → 'unknown'
 */
const toUiValue = (absent: boolean, present: boolean): AbcdeChoice => {
  if (absent && !present) return 'absent';
  if (!absent && present) return 'present';
  return 'unknown';
};

/** Form-ul intern are câmpuri text per literă (nu 10 Boolean-uri). */
type AbcdeFormValues = {
  evaluationDate: dayjs.Dayjs;
  performedBy: string;
  abcdeA: AbcdeChoice;
  abcdeB: AbcdeChoice;
  abcdeC: AbcdeChoice;
  abcdeD: AbcdeChoice;
  abcdeE: AbcdeChoice;
  conclusion: AbcdeConclusion;
  observations?: string;
};

const LETTER_FORM_KEYS: Record<LetterField, keyof AbcdeFormValues> = {
  A: 'abcdeA',
  B: 'abcdeB',
  C: 'abcdeC',
  D: 'abcdeD',
  E: 'abcdeE',
};

const Step5Abcde = () => {
  const { lesions, selectedLesionId, selectLesion, loading } = useDermatoWizard();
  const { user } = useAuthStore();
  const [formAntd] = Form.useForm<AbcdeFormValues>();
  const [evaluation, setEvaluation] = useState<AbcdeEvaluation | null>(null);
  const [loadingEval, setLoadingEval] = useState(false);
  const [saving, setSaving] = useState(false);

  const lesion = useMemo(
    () => lesions.find((l) => l.id === selectedLesionId) ?? null,
    [lesions, selectedLesionId]
  );

  // Load existing ABCDE for selected lesion
  useEffect(() => {
    if (!lesion?.id) {
      setEvaluation(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingEval(true);
        const v = await dermatoApi.abcde.getByLesion(lesion.id!);
        if (cancelled) return;
        setEvaluation(v);
        if (v) {
          // Perechile de Boolean existente → valorile UI per literă.
          formAntd.setFieldsValue({
            evaluationDate: dayjs(v.evaluationDate),
            performedBy: v.performedBy,
            conclusion: v.conclusion,
            observations: v.observations ?? undefined,
            [LETTER_FORM_KEYS.A]: toUiValue(v.asymmetryAbsent, v.asymmetryPresent),
            [LETTER_FORM_KEYS.B]: toUiValue(v.borderRegular, v.borderIrregular),
            [LETTER_FORM_KEYS.C]: toUiValue(v.colorUniform, v.colorNonUniform),
            [LETTER_FORM_KEYS.D]: toUiValue(v.diameterLess6mm, v.diameter6mm),
            [LETTER_FORM_KEYS.E]: toUiValue(v.evolutionStable, v.evolutionModified),
          } as AbcdeFormValues);
        } else {
          formAntd.resetFields();
          formAntd.setFieldsValue({
            evaluationDate: dayjs(),
            performedBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || '',
            conclusion: AbcdeConclusion.REQUIRES_DERMOSCOPY,
          } as Partial<AbcdeFormValues>);
        }
      } catch {
        if (!cancelled) setEvaluation(null);
      } finally {
        if (!cancelled) setLoadingEval(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [lesion?.id, formAntd, user]);

  const persist = async (): Promise<boolean> => {
    if (!lesion?.id) return true;
    try {
      const values = await formAntd.validateFields();
      setSaving(true);
      // Mapare inversă: 5 câmpuri UI → 10 Boolean-uri din DTO.
      const [asymmetryAbsent, asymmetryPresent] = toPair(values.abcdeA);
      const [borderRegular, borderIrregular] = toPair(values.abcdeB);
      const [colorUniform, colorNonUniform] = toPair(values.abcdeC);
      const [diameterLess6mm, diameter6mm] = toPair(values.abcdeD);
      const [evolutionStable, evolutionModified] = toPair(values.abcdeE);
      const dto: CreateAbcdeDto = {
        evaluationDate: values.evaluationDate.toISOString(),
        performedBy: values.performedBy,
        asymmetryAbsent,
        asymmetryPresent,
        borderRegular,
        borderIrregular,
        colorUniform,
        colorNonUniform,
        diameterLess6mm,
        diameter6mm,
        evolutionStable,
        evolutionModified,
        conclusion: values.conclusion,
        observations: values.observations,
      };
      const saved = evaluation?.id
        ? await dermatoApi.abcde.update(evaluation.id, dto)
        : await dermatoApi.abcde.createForLesion(lesion.id, dto);
      setEvaluation(saved);
      return true;
    } catch {
      // Form validation errors shown inline
      return false;
    } finally {
      setSaving(false);
    }
  };

  useRegisterStepSave(persist);

  if (loading) {
    return <Card loading title="Screening ABCDE" />;
  }

  if (lesions.length === 0) {
    return (
      <Card title="Screening ABCDE">
        <Empty
          description={
            <Space direction="vertical">
              <Text>Nu există leziuni definite.</Text>
              <Text type="secondary">Mergi la pasul 3 pentru a adăuga cel puțin o leziune.</Text>
            </Space>
          }
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <SkinOutlined style={{ color: '#1890ff' }} />
          <span>Screening ABCDE — per leziune</span>
        </Space>
      }
    >
      <Form.Item label="Selectează leziunea" required style={{ marginBottom: 16 }}>
        <Select
          value={selectedLesionId ?? undefined}
          onChange={selectLesion}
          placeholder="Alege o leziune din inventar"
          options={lesions.map((l) => ({
            value: l.id,
            label: `${l.clinicalCode} — ${l.location}`,
          }))}
        />
      </Form.Item>

      {loadingEval && <Alert type="info" message="Se încarcă evaluarea ABCDE…" />}

      {!lesion && !loadingEval && (
        <Alert
          type="warning"
          showIcon
          message="Selectează o leziune pentru a continua evaluarea ABCDE."
        />
      )}

      {lesion && !loadingEval && (
        <>
          <Alert
            type="info"
            showIcon
            message={
              <Space>
                <Text>Leziune selectată:</Text>
                <Tag color="blue">{lesion.clinicalCode}</Tag>
                <Text strong>{lesion.location}</Text>
                <Text type="secondary">({lesion.size})</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />

          <Form form={formAntd} layout="vertical">
            <Form.Item name="evaluationDate" label="Data evaluării" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="performedBy" label="Medic evaluator" rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            {LETTERS.map(({ letter, title, absentKey, presentKey }) => (
              <div key={letter}>
                <Divider>{title}</Divider>
                <Form.Item
                  name={LETTER_FORM_KEYS[letter]}
                  label={`Alegere pentru ${letter}`}
                  rules={[{ required: true, message: 'Selectează Absentă, Prezentă sau Necunoscut' }]}
                >
                  <Radio.Group>
                    <Radio value="absent">{`Absentă (${absentKeyToRo(absentKey)})`}</Radio>
                    <Radio value="present">{`Prezentă (${presentKeyToRo(presentKey)})`}</Radio>
                    <Radio value="unknown">Necunoscut</Radio>
                  </Radio.Group>
                </Form.Item>
              </div>
            ))}

            <Divider>Concluzie</Divider>
            <Form.Item name="conclusion" label="Concluzie ABCDE" rules={[{ required: true }]}>
              <Select options={CONCLUSION_OPTIONS} />
            </Form.Item>
            <Form.Item name="observations" label="Observații">
              <Input.TextArea rows={3} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" onClick={() => void persist()} loading={saving}>
                  {evaluation ? 'Actualizează ABCDE' : 'Salvează ABCDE'}
                </Button>
                {evaluation && (
                  <Text type="success">✓ Evaluare existentă — {evaluation.id.slice(0, 8)}</Text>
                )}
              </Space>
            </Form.Item>
          </Form>
        </>
      )}
    </Card>
  );
};

// Etichete românești pentru perechile de Boolean — afișate în paranteză lângă Absentă/Prezentă,
// ca medicul să vadă exact ce se salvează în DTO.
function absentKeyToRo(key: keyof CreateAbcdeDto): string {
  switch (key) {
    case 'asymmetryAbsent':
      return 'fără asimetrie';
    case 'borderRegular':
      return 'margini regulate';
    case 'colorUniform':
      return 'culoare uniformă';
    case 'diameterLess6mm':
      return 'diametru sub 6 mm';
    case 'evolutionStable':
      return 'stabilă în timp';
    default:
      return String(key);
  }
}

function presentKeyToRo(key: keyof CreateAbcdeDto): string {
  switch (key) {
    case 'asymmetryPresent':
      return 'cu asimetrie';
    case 'borderIrregular':
      return 'margini neregulate';
    case 'colorNonUniform':
      return 'culoare neuniformă';
    case 'diameter6mm':
      return 'diametru ≥ 6 mm';
    case 'evolutionModified':
      return 'modificată recent';
    default:
      return String(key);
  }
}

export default Step5Abcde;